import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiArrowLeft, HiHome } from 'react-icons/hi';
import { Link, useNavigate } from 'react-router-dom';
import './PageLayout.scss';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  href?: string; // Support both for backward compatibility
}

export interface PageLayoutProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  children: React.ReactNode;
  headerActions?: React.ReactNode;
  className?: string;
  showBackButton?: boolean;
  isIndexPage?: boolean;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  subtitle,
  breadcrumbs = [],
  children,
  headerActions,
  className = '',
  showBackButton,
  isIndexPage = false
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  // Auto-determine if back button should be shown
  const shouldShowBackButton = showBackButton !== undefined ? showBackButton : !isIndexPage;

  const handleBack = () => {
      navigate(-1);
  };

  return (
    <div className={`modern-page-layout ${isRTL ? 'rtl' : 'ltr'} ${className}`}>
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <nav className="breadcrumbs">
          <ol className="breadcrumb-list">
            <li className="breadcrumb-item">
              <Link to="/" className="breadcrumb-link">
                <HiHome />
                <span>{t('menu.homepage')}</span>
              </Link>
            </li>
            {breadcrumbs.map((item, index) => (
              <li key={index} className="breadcrumb-item">
                <span className="breadcrumb-separator">/</span>
                {(item.path || item.href) ? (
                  <Link to={item.path || item.href || '#'} className="breadcrumb-link">
                    {item.label}
                  </Link>
                ) : (
                  <span className="breadcrumb-current">{item.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Page Header */}
      <header className="page-header">
        <div className="header-content">
          <div className="header-left">
            {shouldShowBackButton && (
              <button className="back-button" onClick={handleBack}>
                <HiArrowLeft />
              </button>
            )}
            <div className="header-text">
              <h1 className="page-title">{title}</h1>
              {subtitle && <p className="page-subtitle">{subtitle}</p>}
            </div>
          </div>
          
          {headerActions && (
            <div className="header-actions">
              {headerActions}
            </div>
          )}
        </div>
      </header>

      {/* Page Content */}
      <main className="page-content">
        {children}
      </main>
    </div>
  );
};

export default PageLayout;
